import { SpinSpeed } from '@GFTypes/GameStoreTypes';
import { EOrientation } from '@Managers/Device/UseWindowDimensions.types';
import { EActionName } from '@Managers/Events/EActionName';
import { DeviceType, LoggerLevel, TLoggerParameters } from '@Managers/Logger';

export const HTTP_HEADER_DEFAULT = {
    Accept: '*/*',
    'Content-Type': 'application/json'
};

export type TCreditionals = {
    [key: string]: any;
    playerId: string;
    gameId: string;
    casinoId: string;
    walletId: string;
    languageCode: string;
    isDemoPlay: boolean;
    walletToken: string;
    clientPlatform: string;
    casinoTempToken: string;
    params: {
        countryCode?: string;
        isInternalPlayer?: boolean;
    };
};

export type TUserActionResponse = {
    error?: string;
    code?: string;
    cycleId?: string;
    account?: {
        currency?: {
            code?: string;
            exponent?: number;
        };
        balance?: number;
        bonuses?: Array<any>;
    };
    bets?: {
        steps: Array<number>;
        default: number;
    };
    resume?: {
        actionName: string;
        win: number;
        nextActions: Array<string>;
        bet: number;
    };
    result?: {
        win?: number;
        outcome?: Array<{
            win?: number;
            gameType: number;
            steps?: any;
        }>;
        nextActions?: Array<string>;
    };
    spin?: string[];
};

export enum Environment {
    TEST = 'test',
    STAGE = 'stage',
    PROD = 'prod'
}

export enum GameRenderer {
    WEBGL = 'WEBGL',
    CANVAS = 'CANVAS'
}

export enum HttpMethodType {
    POST = 'POST'
}

export interface IKibanaUserInformation {
    playerId?: string;
    gameId?: string;
    casinoId?: string;
    walletId?: string;
    isDemoPlay?: boolean;
    walletToken?: string;
    clientPlatform?: string;
    gameName?: string;
}

export interface KibanaLoggerOptions {
    url: string;
    env?: Environment;
    app?: string;
}

interface IKibanaBasePayload {
    level?: LoggerLevel;
    userData: IKibanaUserInformation;
    env: string;
    app: string;
    log: TLoggerParameters;
    screen: {
        width: number;
        height: number;
        orientation: EOrientation;
        dpr: number;
    };
}

export interface IVersionParts {
    raw: string;
    major: number;
    minor: number;
    patch: number;
}

export interface IUserAgentInfo {
    os: {
        name: string;
        version: IVersionParts;
    };
    browser: {
        name: string;
        version: IVersionParts;
    };
    engine: {
        name: string | null;
        version: IVersionParts;
    };
    device: {
        type: DeviceType;
        vendor: string | null;
        model: string | null;
    };
}

export interface IKibanaGameStartPayload extends IKibanaBasePayload {
    deviceInfo: {
        userAgent: string;
        userAgentInfo: IUserAgentInfo;
        telemetry: {
            gameRenderer: GameRenderer;
            cpuCores: number | null;
            memoryGB: number | null;
            gpuVendor: string | null;
            gpuRenderer: string | null;
        };
    };
}

export interface IKibanaSpinSummaryPayload extends IKibanaBasePayload {
    actionName: EActionName;
    statistics: IStatistics;
    settings: {
        wcaMode: boolean;
        leftHandMode: boolean;
        soundMuted: boolean;
        fullscreen: boolean;
        spinSpeed: SpinSpeed;
        autoMode: boolean;
    };
}

export interface IKibanaErrorPayload extends IKibanaBasePayload {
    errorInformation: string;
}

export interface IStatistics {
    bet: number;
    responseTimes: IResponseTimes;
    spinTimes: ISpinTimes;
    quickStop: boolean;
    winAmount?: number;
    avgFps?: number;
    minFps?: number;
    maxFps?: number;
}

export interface IResponseTimes {
    spin: number;
    take?: number;
    total: number;
}

export interface ISpinTimes {
    normalSpin: number;
    tension?: number;
    total: number;
}

export type KibanaLogPayload = IKibanaGameStartPayload | IKibanaSpinSummaryPayload | IKibanaErrorPayload;
